// A4-Vorschau eines Monatsberichts mit Lupe zum Aufziehen.
//
// Echte Vorschau statt Platzhalterbild: Browser rendern PDFs in einem <iframe>
// von sich aus, das kostet keine zusätzliche Bibliothek. Das Blatt wird per
// CSS-Transform verkleinert und behält das A4-Verhältnis (1:√2).
//
// Das PDF wird erst geladen, wenn die Kachel ins Bild kommt — auf einer Seite
// mit zehn Kunden wären zehn sofortige Erzeugungen unnötig teuer.

import { useEffect, useRef, useState } from 'react';
import { Download, Loader2, X, ZoomIn } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchReportPdfUrl, openReportPdf } from '@/lib/analytics-api';

interface Props {
  /** Monatsbericht-Modus. Entfällt, wenn `laden` gesetzt ist. */
  month?: string;
  customerId?: string;
  /** Bildbreite der Kachel in Pixeln; die Höhe folgt dem A4-Verhältnis. */
  breite?: number;
  titel?: string;
  /**
   * Eigene Quelle statt des Monatsberichts: liefert eine Blob-URL des PDFs.
   * Damit zeigt dieselbe A4-Vorschau auch die Potenzialanalyse.
   */
  laden?: () => Promise<string>;
  /** Eigener Download-Weg, passend zu `laden`. */
  herunterladen?: () => Promise<void>;
}

const A4 = 1.4142; // Höhe / Breite

export default function PdfVorschau({ month, customerId, breite = 168, titel, laden, herunterladen }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gross, setGross] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const sichtbar = useRef(false);

  // Lazy laden, sobald die Kachel ins Bild kommt
  useEffect(() => {
    const el = box.current;
    if (!el || url || laedt) return;
    const io = new IntersectionObserver((eintraege) => {
      if (!eintraege[0]?.isIntersecting || sichtbar.current) return;
      sichtbar.current = true;
      io.disconnect();
      setLaedt(true);
      (laden ? laden() : fetchReportPdfUrl(month!, customerId))
        .then(setUrl)
        .catch((e) => setFehler(e?.message || 'Vorschau nicht möglich'))
        .finally(() => setLaedt(false));
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [month, customerId, url, laedt, laden]);

  // Blob-URL freigeben, wenn die Kachel verschwindet
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const hoehe = Math.round(breite * A4);
  // Das iframe rendert in A4-Punkten (595 breit) und wird herunterskaliert —
  // so bleibt der Text scharf statt verpixelt.
  const skala = breite / 595;

  useEffect(() => {
    if (!gross) return;
    const zu = (e: KeyboardEvent) => { if (e.key === 'Escape') setGross(false); };
    window.addEventListener('keydown', zu);
    return () => window.removeEventListener('keydown', zu);
  }, [gross]);

  return (
    <>
      <div
        ref={box}
        className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
        style={{ width: breite, height: hoehe }}
      >
        {url ? (
          <div
            style={{
              width: 595,
              height: 842,
              transform: `scale(${skala})`,
              transformOrigin: 'top left',
              pointerEvents: 'none',
            }}
          >
            <iframe
              src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
              title={titel || `Monatsbericht ${month ?? ""}`}
              style={{ width: 595, height: 842, border: 0 }}
            />
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-300">
            {laedt ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {fehler ? <span className="px-3 text-center text-[10px] text-amber-700">{fehler}</span> : null}
          </div>
        )}

        {/* Lupe: liegt über dem Blatt, wird beim Überfahren deutlicher */}
        <button
          onClick={() => url && setGross(true)}
          disabled={!url}
          title="Größer ansehen"
          aria-label="Bericht größer ansehen"
          className="absolute inset-0 flex items-center justify-center bg-gray-900/0 transition group-hover:bg-gray-900/20 disabled:cursor-default"
        >
          {url && (
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-gray-700 opacity-0 shadow-md transition group-hover:opacity-100">
              <ZoomIn className="h-4 w-4" />
            </span>
          )}
        </button>
      </div>

      {gross && url && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-gray-900/80 p-4 sm:p-8"
          onClick={() => setGross(false)}
        >
          <div className="mx-auto flex w-full max-w-4xl flex-1 flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center gap-3">
              <p className="text-sm font-medium text-white">{titel || `Monatsbericht ${month ?? ""}`}</p>
              <button
                onClick={async () => {
                  try {
                    if (herunterladen) await herunterladen();
                    else await openReportPdf(month!, customerId);
                  } catch (e: any) {
                    toast.error(e?.message || 'Download fehlgeschlagen');
                  }
                }}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20"
              >
                <Download className="h-3.5 w-3.5" /> Herunterladen
              </button>
              <button
                onClick={() => setGross(false)}
                aria-label="Schließen"
                className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20"
              >
                <X className="h-3.5 w-3.5" /> Schließen
              </button>
            </div>
            <iframe
              src={url}
              title={titel || `Monatsbericht ${month ?? ""}`}
              className="flex-1 rounded-lg bg-white"
              style={{ border: 0, minHeight: '60vh' }}
            />
          </div>
        </div>
      )}
    </>
  );
}
